import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC } from "react";
// import { css } from "styled-components"; // Store Custom Code

/* Store Custom Code - Start */
import { injectCss } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import styled, { css } from "styled-components";
/* Store Custom Code - End */

interface OwnProps {
    productNumber: string;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showLabel?: boolean;
    showManufacturerItem?: boolean;
    extendedStyles?: ProductPartNumbersStyles;
    showProductNumber?: boolean;
    // Store Custom Code
    productIndustryNumber?: string;
}

type Props = OwnProps;

export interface ProductPartNumbersStyles {
    condensed?: boolean;
    container?: GridContainerProps;
    erpNumberGridItem?: GridItemProps;
    erpNumberLabelText?: TypographyPresentationProps;
    erpNumberValueText?: TypographyPresentationProps;
    customerNameGridItem?: GridItemProps;
    customerNameLabelText?: TypographyPresentationProps;
    customerNameValueText?: TypographyPresentationProps;
    manufacturerItemGridItem?: GridItemProps;
    manufacturerItemLabelText?: TypographyPresentationProps;
    manufacturerItemValueText?: TypographyPresentationProps;
    // Store Custom Code
    partNumberGridItem?: GridItemProps;
    partNumberValueText?: TypographyPresentationProps;
    labelTextStyle?: TypographyPresentationProps;
    valueTextStyle?: TypographyPresentationProps;
}

/* Store Custom Code - Start */
const labelStyles = {
    css: css`
        flex: 0 0 auto;
        margin-right: 5px;
        ${({ theme }) => {
            return breakpointValuesMediaQueries(
                theme,
                [{ values: [".9", null, "inherit", null, null], properties: ["line-height"] }],
                mediaOptions.min,
            );
        }}
    `,
    size: TYPOGRAPHY.size.smallest,
    weight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.light,
};

const valueStyles = {
    css: css`
        flex: 1 1 auto;
        min-width: 0;
        font-weight: ${TYPOGRAPHY.weight.medium};
        ${wrapInContainerStyles}
        ${({ theme }) => {
            return breakpointValuesMediaQueries(
                theme,
                [{ values: [".9", null, "inherit", null, null], properties: ["line-height"] }],
                mediaOptions.min,
            );
        }}
    `,
    size: TYPOGRAPHY.size.smallest,
    color: COLORS.text.light,
};

const gridNumberStyles = {
    css: css`
        ${({ theme }) =>
            breakpointValuesMediaQueries(
                theme,
                [
                    { values: ["100%", null, "auto", null, null], properties: ["flex-basis"] },
                    { values: ["5px 0", null, "0", null, null], properties: ["padding"] },
                ],
                mediaOptions.min,
            )}
    `,
};

const LabelText = styled(Typography as any)<InjectableCss>`
    ${injectCss}
`;

const ValueText = styled(Typography as any)<InjectableCss>`
    ${injectCss}
`;
/* Store Custom Code - End */

export const productPartNumbersStyles: ProductPartNumbersStyles = {
    /* Store Custom Code - Start */
    container: {
        gap: 0,
        css: css`
            margin-bottom: 5px;
        `,
    },
    partNumberValueText: {
        ...valueStyles,
    },
    labelTextStyle: {
        ...labelStyles,
    },
    valueTextStyle: {
        ...valueStyles,
    },
    partNumberGridItem: {
        align: "middle",
        css: css`
            ${gridNumberStyles?.css}
            &:not(:last-child) {
                ${ValueText} {
                    &::after {
                        content: "|";
                        margin-left: 12px;
                        ${({ theme }) =>
                            breakpointValuesMediaQueries(
                                theme,
                                [{ values: ["none", null, "inline", null, null], properties: ["display"] }],
                                mediaOptions.min,
                            )}
                    }

                    ${({ theme }) =>
                        breakpointValuesMediaQueries(
                            theme,
                            [{ values: ["0", null, "10px", null, null], properties: ["margin-right"] }],
                            mediaOptions.min,
                        )}
                }
            }
        `,
    },
    /* Store Custom Code - End */
};

const ProductPartNumbers: FC<Props> = ({
    productNumber,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
    showLabel = true,
    extendedStyles,
    showProductNumber = true,
    // Store Custom Code
    productIndustryNumber,
}) => {
    const [styles] = React.useState(() => mergeToNew(productPartNumbersStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0); // Store Custom Code
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    return (
        <GridContainer {...styles.container}>
            {/* Store Custom Code - Start */}
            <GridItem width={gridItemWidth as GridWidths} {...styles.partNumberGridItem}>
                <ValueText {...styles.partNumberValueText}>{productNumber}</ValueText>
            </GridItem>
            {productIndustryNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.partNumberGridItem}>
                    <LabelText {...styles.labelTextStyle}>{translate("Industry #")}</LabelText>
                    <ValueText {...styles.valueTextStyle}>{productIndustryNumber}</ValueText>
                </GridItem>
            )}
            {displayCustomerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.partNumberGridItem}>
                    <ValueText {...styles.valueTextStyle}>{customerProductNumber}</ValueText>
                </GridItem>
            )}
            {displayManufacturerNumber && (
                <GridItem width={gridItemWidth as GridWidths} {...styles.partNumberGridItem}>
                    {showLabel && <LabelText {...styles.labelTextStyle}>{translate("MFG #")}</LabelText>}
                    <ValueText {...styles.valueTextStyle}>{manufacturerItem}</ValueText>
                </GridItem>
            )}
            {/* Store Custom Code - End */}
        </GridContainer>
    );
};

export default ProductPartNumbers;
